import { TIME_ZONES_ADDITIONS } from 'utils/datetime';

import { ALL_GROUPS_ID } from './components/TimeLineFilter/const';
import { EXTRA_BOARD_CLASSIFICATION_OPTION } from './components/TimeLineFilter/OperatorTypeFilter/const';
import { DEFAULT_PAGE_SIZE, normalizePageSize } from './const';

const STORAGE_KEY = 'crewTimelineFilters';
const DEFAULT_OPERATOR_TYPE = EXTRA_BOARD_CLASSIFICATION_OPTION;
const DEFAULT_DISPLAY_DAYS = 1;
const DEFAULT_TIMEZONE = TIME_ZONES_ADDITIONS.myTime;

const extractCityId = (city) => {
	if (typeof city === 'string') return city;

	return city.value || city.id || city.key;
};

const deserializeCitiesToFormValues = (cities) => {
	if (!Array.isArray(cities) || cities.length === 0) return [];

	return cities.map((city) => ({
		title: city.name || city.title || '',
		value: city.value || city.id || city.key,
		key: city.key || city.value || city.id,
		id: city.id || city.value || city.key,
		name: city.name || city.title || '',
	}));
};

const deserializeCitiesToIds = (cities) => {
	if (!Array.isArray(cities) || cities.length === 0) return [];

	return cities.map(extractCityId).filter(Boolean);
};

/**
 * @typedef {Object} PersistedFilters
 * @property {string} group
 * @property {string} status
 * @property {Array} locationAvailabilityCity
 * @property {string} operatorType
 * @property {number} displayDays
 * @property {number} pageSize
 * @property {{ name: string, value: string }} selectedTimezone
 */

/**
 * Saves filter state to localStorage.
 * @param {Object} params
 * @param {Object} params.filters - the store's filters object
 * @param {number} params.displayDays
 * @param {number} params.pageSize
 * @param {{ name: string, value: string }} params.selectedTimezone
 */
const saveFilters = ({ filters, displayDays, pageSize, selectedTimezone }) => {
	try {
		const data = {
			group: filters.groupsIds?.[0] || '',
			status: filters.status || '',
			locationAvailabilityCity: filters.locationAvailabilityCity || [],
			operatorType: filters.operatorType || DEFAULT_OPERATOR_TYPE,
			displayDays: displayDays || DEFAULT_DISPLAY_DAYS,
			pageSize: normalizePageSize(pageSize),
			selectedTimezone: selectedTimezone || DEFAULT_TIMEZONE,
		};

		localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
	} catch (error) {
		console.error('[CrewTimeline] Failed to save filters:', error);
	}
};

/** @returns {PersistedFilters | null} */
const loadFilters = () => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);

		if (!stored) return null;

		return JSON.parse(stored);
	} catch (_error) {
		return null;
	}
};

const clearFilters = () => {
	localStorage.removeItem(STORAGE_KEY);
};

/**
 * Converts store state to URL query params.
 * Only includes non-default values.
 * @param {Object} params
 * @param {Object} params.filters
 * @param {number} params.displayDays
 * @param {number} params.pageSize
 * @param {{ name: string, value: string }} params.selectedTimezone
 * @returns {Object} key-value pairs for URLSearchParams
 */
const toQueryParams = ({ filters, displayDays, pageSize, selectedTimezone }) => {
	const params = {};

	if (filters.operatorType && filters.operatorType !== DEFAULT_OPERATOR_TYPE) {
		params.operatorType = filters.operatorType;
	}

	if (displayDays !== DEFAULT_DISPLAY_DAYS) {
		params.displayDays = String(displayDays);
	}

	const normalizedPageSize = normalizePageSize(pageSize);

	if (normalizedPageSize !== DEFAULT_PAGE_SIZE) {
		params.pageSize = String(normalizedPageSize);
	}

	if (selectedTimezone?.value && selectedTimezone.value !== DEFAULT_TIMEZONE.value) {
		params.tz = selectedTimezone.value;
		params.tzName = selectedTimezone.name;
	}

	if (filters.groupsIds?.length > 0 && filters.groupsIds[0] !== ALL_GROUPS_ID) {
		params.group = filters.groupsIds[0];
	}

	if (filters.status) {
		params.status = filters.status;
	}

	if (filters.locationAvailabilityCityIds?.length > 0) {
		params.cities = filters.locationAvailabilityCityIds.join(',');
	}

	return params;
};

/**
 * Parses URLSearchParams into a partial filter state.
 * @param {URLSearchParams} searchParams
 * @returns {{
 *   hasParams: boolean,
 *   operatorType?: string,
 *   displayDays?: number,
 *   pageSize?: number,
 *   selectedTimezone?: Object,
 *   group?: string,
 *   status?: string,
 *   cityIds?: string[]
 * }}
 */
const fromQueryParams = (searchParams) => {
	const operatorType = searchParams.get('operatorType');
	const displayDays = searchParams.get('displayDays');
	const pageSize = searchParams.get('pageSize');
	const tz = searchParams.get('tz');
	const tzName = searchParams.get('tzName');
	const group = searchParams.get('group');
	const status = searchParams.get('status');
	const cities = searchParams.get('cities');
	const hasParams = Boolean(
		operatorType || displayDays || pageSize || tz || group || status || cities
	);

	return {
		hasParams,
		...(operatorType && { operatorType }),
		...(displayDays && { displayDays: Number(displayDays) || DEFAULT_DISPLAY_DAYS }),
		...(pageSize && { pageSize: normalizePageSize(pageSize) }),
		...(tz && { selectedTimezone: { name: tzName || tz, value: tz } }),
		...(group && group !== ALL_GROUPS_ID && { group }),
		...(status && { status }),
		...(cities && { cityIds: cities.split(',').filter(Boolean) }),
	};
};

export {
	STORAGE_KEY,
	DEFAULT_OPERATOR_TYPE,
	DEFAULT_DISPLAY_DAYS,
	DEFAULT_TIMEZONE,
	saveFilters,
	loadFilters,
	clearFilters,
	deserializeCitiesToFormValues,
	deserializeCitiesToIds,
	toQueryParams,
	fromQueryParams,
};
